"use client";
import Image from '@/components/ui/MediaImage';
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { shouldEmbedBlockPropsInDom } from "@/lib/embed-block-props-dom";

interface WithImagesCtaProps {
    leftImage?: { mediaId?: number; url?: string; alt?: string };
    rightImage?: { mediaId?: number; url?: string; alt?: string };
    title?: string;
    description?: string;
    __grapesPreview?: boolean;
}

export default function WithImagesCta({ 
    leftImage,
    rightImage,
    title = 'Discover Every Corner Of The World With Us.',
    description = 'Join us on an extraordinary journey as we explore every corner of the world together. Our adventures will take you to hidden gems, iconic landmarks, and breathtaking landscapes, offering unforgettable experiences and memories. Discover the beauty and diversity of our planet with us.',
    __grapesPreview = false,
    ...restProps
}: WithImagesCtaProps) {
    const [componentProps, setComponentProps] = useState<WithImagesCtaProps>({});

    // Component hydration: data-component-props'tan props'ları al
    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) return;
        const updateProps = () => {
            const element = document.querySelector('[data-component-path="sections/WithImagesCta"]');
            if (element) {
                const propsAttr = element.getAttribute('data-component-props');
                if (propsAttr) {
                    try {
                        const props = JSON.parse(propsAttr);
                        setComponentProps(props);
                    } catch {
                        // Props parse hatası
                    }
                }
            }
        };

        updateProps();

        const observer = new MutationObserver(updateProps);
        const element = document.querySelector('[data-component-path="sections/WithImagesCta"]');
        if (element) {
            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-component-props']
            });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    // Props'tan veya componentProps'tan değerleri al
    const finalLeftImage = componentProps.leftImage || leftImage;
    const finalRightImage = componentProps.rightImage || rightImage;
    const finalTitle = componentProps.title || title;
    const finalDescription = componentProps.description || description;
    
    // URL'leri transform et (medya proxy kullanımı için)
    const leftImageUrl = finalLeftImage?.url ? transformMediaUrl(finalLeftImage.url, null) : null;
    const rightImageUrl = finalRightImage?.url ? transformMediaUrl(finalRightImage.url, null) : null;
    
    // Backend render modunu kontrol et
    const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
    
    // Backend render attribute'larını oluştur (sadece backend'de render edilirken eklenir)
    const backendAttributes = getBackendRenderAttributes(
        'sections/WithImagesCta',
        { leftImage, rightImage, title: finalTitle, description: finalDescription },
        isBackendRender
    );

    return (
        <div {...backendAttributes} className="flex flex-row gap-14 items-center my-[190px]">
            <motion.div 
                className="flex-1 relative min-h-[380px] w-full max-w-[560px]"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                    type: "spring" as const, 
                    stiffness: 80, 
                    damping: 20,
                    delay: 0.2
                }}
            >
                {/* Alttaki görsel: sağ altta, daha geniş yatay dikdörtgen (taban) */}
                {rightImageUrl && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        whileHover={{ rotate: -1 }}
                        className="absolute right-0 bottom-0 rounded-lg overflow-hidden w-[420px] h-[280px] z-0"
                    >
                        <Image 
                            src={rightImageUrl} 
                            alt={finalRightImage?.alt || "right-img"} 
                            width={420} 
                            height={280}  
                            className="w-full h-full object-cover rounded-lg"
                            unoptimized={rightImageUrl.startsWith('/api/media/proxy/')}
                        />
                    </motion.div>
                )}
                {/* Üstteki görsel: sol üstte, alttaki görselin sol üstünü hafifçe örter */}
                {leftImageUrl && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                        whileHover={{ rotate: 1 }}
                        className="absolute left-0 top-0 rounded-lg overflow-hidden w-[380px] h-[260px] z-10"
                    >
                        <Image 
                            src={leftImageUrl} 
                            alt={finalLeftImage?.alt || "left-img"} 
                            width={380} 
                            height={260}  
                            className="w-full h-full object-cover rounded-lg"
                            unoptimized={leftImageUrl.startsWith('/api/media/proxy/')}
                        />
                    </motion.div>
                )}
            </motion.div>
            <motion.div 
                className="flex-1 flex flex-col justify-between gap-16"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                    type: "spring" as const, 
                    stiffness: 80, 
                    damping: 20,
                    delay: 0.3
                }}
            >
                <motion.h2 
                    className="font-bold text-alsero-second text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight sm:leading-snug"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    data-prop-path="title"
                >
                    {finalTitle} 
                </motion.h2>
                <motion.p 
                    className="text-alsero-light-gray text-base sm:text-lg md:text-xl leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    data-prop-path="description"
                >
                    {finalDescription}
                </motion.p>
            </motion.div>
        </div>
    )
}